import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Stack } from "@mui/material";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";

import {
  cancelSalesImportDraft,
  confirmSalesImportRegularizations,
  createSalesImportDraft,
  getSalesImportMatching,
  getSalesImportProductOptions,
  integrateSalesImport,
  recalculateSalesImportPreview,
  saveSalesImportResolutions,
} from "../api/salesImportsApi";
import SalesImportDiscardDialog from "../components/SalesImportDiscardDialog";
import SalesImportDraftSummary from "../components/SalesImportDraftSummary";
import SalesImportFilePanel from "../components/SalesImportFilePanel";
import SalesImportIntegrationDialog from "../components/SalesImportIntegrationDialog";
import SalesImportStepOne from "../components/SalesImportStepOne";
import SalesImportStepTwo from "../components/SalesImportStepTwo";
import SalesImportStepThree from "../components/SalesImportStepThree";
import SalesImportStepFour from "../components/SalesImportStepFour";
import SalesImportWizardShell from "../components/SalesImportWizardShell";
import {
  normalizeSalesImportDraft,
  normalizeSalesImportUploadError,
  validateSalesImportFile,
} from "../utils/salesImportDraftModel";
import { getSalesImportErrorMessage } from "../utils/salesImportError";
import {
  canContinueSalesImportStepTwo,
  normalizeSalesImportMatching,
  normalizeSalesImportPreview,
  normalizeSalesImportProductOptions,
  normalizeSalesImportRegularizationPlan,
  orderSalesImportDescriptions,
} from "../utils/salesImportStepTwoModel";

const SALES_IMPORTS_PATH = "/stock/operacion/ventas-importadas";
const LIST_STATE_KEY = "salesImportListState";

function integrationRequestId(draftId) {
  const uuid = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `sales-import-${draftId}-${uuid}`;
}

function integrationPreparationSignature(draft, preview, regularizationPlan, treasuryConfig) {
  return JSON.stringify({
    draftId: draft.id,
    version: draft.version,
    previewRevision: preview.previewRevision,
    planVersion: regularizationPlan?.planVersion ?? null,
    periodId: treasuryConfig.period.id,
    categoryId: treasuryConfig.category.id,
    allocations: treasuryConfig.allocations
      .map(({ accountId, amount }) => [Number(accountId), String(amount)])
      .sort(([first], [second]) => first - second),
  });
}

function integrationErrorCode(error) {
  return error?.response?.data?.code || error?.data?.code || "";
}

function planMatchesPreview(plan, preview) {
  return Boolean(
    plan?.confirmedAt &&
    plan.draftId === preview?.draftId &&
    plan.draftVersion === preview?.version &&
    plan.previewRevision === preview?.previewRevision
  );
}

function localValidationResult(message) {
  return {
    status: null,
    code: "INVALID_LOCAL_FILE",
    message,
    conflictCount: 0,
    integratedImportId: null,
    errors: [
      {
        id: "INVALID_LOCAL_FILE-general-0",
        row: null,
        column: "Archivo",
        code: "INVALID_LOCAL_FILE",
        message,
        integratedImportId: null,
        receipt: null,
      },
    ],
  };
}

export default function ImportSalesWizardPage() {
  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const returnListState = useRef(location.state?.[LIST_STATE_KEY] || null).current;
  const uploadController = useRef(null);
  const cleanupController = useRef(null);
  const stepTwoController = useRef(null);
  const integrationController = useRef(null);
  const integrationInFlight = useRef(false);
  const integrationKey = useRef(null);
  const rememberedStockResolutions = useRef(new Map());
  const descriptionOrderRef = useRef({ draftId: null, descriptions: [] });
  const stepTwoRequestId = useRef(0);
  const uploadingRef = useRef(false);
  const navigationAllowedRef = useRef(false);
  const [file, setFile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [operationError, setOperationError] = useState("");
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [dialogMode, setDialogMode] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [matching, setMatching] = useState(null);
  const [preview, setPreview] = useState(null);
  const [regularizationPlan, setRegularizationPlan] = useState(null);
  const [stepTwoLoading, setStepTwoLoading] = useState(false);
  const [stepTwoError, setStepTwoError] = useState("");
  const [stepTwoSaving, setStepTwoSaving] = useState(false);
  const [stepTwoHasPendingEdit, setStepTwoHasPendingEdit] = useState(false);
  const [stepTwoNotice, setStepTwoNotice] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [productOptionsLoading, setProductOptionsLoading] = useState(false);
  const [productOptionsError, setProductOptionsError] = useState("");
  const [productOptionsRetry, setProductOptionsRetry] = useState(0);
  const [treasuryConfig, setTreasuryConfig] = useState(null);
  const [treasuryForm, setTreasuryForm] = useState(null);
  const [stepThreeSaving, setStepThreeSaving] = useState(false);
  const [stepThreeNotice, setStepThreeNotice] = useState("");
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationError, setIntegrationError] = useState("");

  const hasActiveDraft = Boolean(draft?.id);
  const busy = status === "uploading" || cleanupLoading || stepTwoLoading || stepTwoSaving || stepThreeSaving || integrationLoading;
  const stepTwoReady = canContinueSalesImportStepTwo(preview, regularizationPlan);
  const treasuryReady = Boolean(treasuryConfig?.valid && treasuryConfig?.persisted && treasuryConfig?.draftVersion === draft?.version);
  const readyToIntegrate = Boolean(draft?.id && status === "success" && stepTwoReady && treasuryReady);

  useEffect(() => {
    if (currentStep !== 2) return undefined;
    const controller = new AbortController();
    setProductOptions([]);
    setProductOptionsLoading(true);
    setProductOptionsError("");
    const timer = setTimeout(async () => {
      try {
        const response = await getSalesImportProductOptions({
          search: productSearch || undefined,
          limit: 100,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setProductOptions(normalizeSalesImportProductOptions(response));
        }
      } catch (requestError) {
        if (!controller.signal.aborted) {
          setProductOptions([]);
          setProductOptionsError(getSalesImportErrorMessage(requestError, "No se pudieron cargar los Productos de venta."));
        }
      } finally {
        if (!controller.signal.aborted) setProductOptionsLoading(false);
      }
    }, productSearch ? 250 : 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [currentStep, productSearch, productOptionsRetry]);
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasActiveDraft &&
      !navigationAllowedRef.current &&
      `${currentLocation.pathname}${currentLocation.search}` !==
        `${nextLocation.pathname}${nextLocation.search}`
  );
  const blockerState = navigationBlocker.state;
  const resetBlockedNavigation = navigationBlocker.reset;

  useEffect(() => {
    if (blockerState === "blocked") {
      if (integrationLoading) {
        resetBlockedNavigation?.();
        return;
      }
      setOperationError("");
      setDialogMode("blocked");
    }
  }, [blockerState, integrationLoading, resetBlockedNavigation]);

  useEffect(() => {
    if (!hasActiveDraft || navigationAllowedRef.current) return undefined;
    const handleBeforeUnload = (event) => {
      if (navigationAllowedRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasActiveDraft]);

  useEffect(
    () => () => {
      uploadController.current?.abort();
      cleanupController.current?.abort();
      stepTwoController.current?.abort();
      integrationController.current?.abort();
    },
    []
  );

  function clearDraftState() {
    integrationKey.current = null;
    rememberedStockResolutions.current.clear();
    descriptionOrderRef.current = { draftId: null, descriptions: [] };
    setIntegrationOpen(false);
    setIntegrationError("");
    stepTwoRequestId.current += 1;
    stepTwoController.current?.abort();
    stepTwoController.current = null;
    setCurrentStep(1);
    setMatching(null);
    setPreview(null);
    setRegularizationPlan(null);
    setTreasuryConfig(null);
    setTreasuryForm(null);
    setStepThreeNotice("");
    setStepTwoLoading(false);
    setStepTwoError("");
    setStepTwoSaving(false);
    setStepTwoHasPendingEdit(false);
    setStepTwoNotice("");
    setProductSearch("");
    setProductOptions([]);
    setProductOptionsError("");
    setFile(null);
    setDraft(null);
    setStatus("idle");
    setError(null);
    setOperationError("");
  }

  async function deleteDraft(draftId, { reportError = true } = {}) {
    const controller = new AbortController();
    cleanupController.current = controller;
    setCleanupLoading(true);
    try {
      await cancelSalesImportDraft(draftId, { signal: controller.signal });
      return { deleted: true, aborted: false, message: "" };
    } catch (requestError) {
      if (controller.signal.aborted) {
        return { deleted: false, aborted: true, message: "" };
      }
      const message = getSalesImportErrorMessage(
        requestError,
        "No se pudo eliminar el borrador. Podés reintentar o salir y dejar que expire."
      );
      if (reportError) setOperationError(message);
      return { deleted: false, aborted: false, message };
    } finally {
      if (cleanupController.current === controller) {
        cleanupController.current = null;
        setCleanupLoading(false);
      }
    }
  }

  async function requestDraft(nextFile) {
    const controller = new AbortController();
    uploadController.current = controller;
    uploadingRef.current = true;

    try {
      const response = await createSalesImportDraft(nextFile, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) {
        return { draft: null, error: null, aborted: true };
      }
      return {
        draft: normalizeSalesImportDraft(response),
        error: null,
        aborted: false,
      };
    } catch (requestError) {
      if (controller.signal.aborted) {
        return { draft: null, error: null, aborted: true };
      }
      return {
        draft: null,
        error: normalizeSalesImportUploadError(requestError),
        aborted: false,
      };
    } finally {
      if (uploadController.current === controller) {
        uploadingRef.current = false;
        uploadController.current = null;
      }
    }
  }

  async function uploadFile(nextFile) {
    rememberedStockResolutions.current.clear();
    setFile(nextFile);
    setDraft(null);
    setError(null);
    setOperationError("");
    setStatus("uploading");

    const result = await requestDraft(nextFile);
    if (result.aborted) return;
    if (result.error) {
      setError(result.error);
      setStatus("error");
      return;
    }
    setDraft(result.draft);
    setStatus("success");
  }

  async function replaceFile(nextFile) {
    const previousFile = file;
    const previousDraft = draft;
    const previousStatus = status;
    const previousError = error;
    setFile(nextFile);
    setError(null);
    setOperationError("");
    setStatus("uploading");

    const uploaded = await requestDraft(nextFile);
    if (uploaded.aborted) return;
    if (uploaded.error) {
      setFile(previousFile);
      setDraft(previousDraft);
      setStatus(previousStatus);
      setError(previousError);
      setOperationError(
        `No se pudo reemplazar el archivo. Se conservó el borrador anterior. ${uploaded.error.message}`
      );
      return;
    }

    const previousDeletion = await deleteDraft(previousDraft.id, {
      reportError: false,
    });
    if (previousDeletion.aborted) return;
    if (!previousDeletion.deleted) {
      const compensation = await deleteDraft(uploaded.draft.id, {
        reportError: false,
      });
      if (compensation.aborted) return;
      setFile(previousFile);
      setDraft(previousDraft);
      setStatus(previousStatus);
      setError(previousError);
      setOperationError(
        compensation.deleted
          ? `${previousDeletion.message} Se conservó el borrador anterior.`
          : `${previousDeletion.message} Se conservó el borrador anterior, pero tampoco se pudo eliminar el nuevo borrador; el backend lo descartará cuando expire.`
      );
      return;
    }

    setDraft(uploaded.draft);
    rememberedStockResolutions.current.clear();
    setStatus("success");
  }

  async function handleFile(nextFile) {
    if (uploadingRef.current || cleanupLoading) return;

    const validationMessage = validateSalesImportFile(nextFile);
    if (validationMessage) {
      if (hasActiveDraft) {
        setOperationError(validationMessage);
      } else {
        setFile(null);
        setDraft(null);
        setStatus("error");
        setError(localValidationResult(validationMessage));
      }
      return;
    }

    if (hasActiveDraft) {
      if (currentStep > 1) {
        stepTwoRequestId.current += 1;
        stepTwoController.current?.abort();
        setCurrentStep(1);
        setMatching(null);
        setPreview(null);
        setRegularizationPlan(null);
        setTreasuryConfig(null);
        setTreasuryForm(null);
        setStepTwoLoading(false);
        setStepTwoHasPendingEdit(false);
        setStepTwoError("");
        setStepTwoNotice("");
      }
      await replaceFile(nextFile);
      return;
    }
    await uploadFile(nextFile);
  }

  async function loadStepTwo(
    activeDraft,
    { preserveNotice = false, retryVersionConflict = true } = {}
  ) {
    const requestId = ++stepTwoRequestId.current;
    stepTwoController.current?.abort();
    const controller = new AbortController();
    stepTwoController.current = controller;
    setStepTwoLoading(true);
    setStepTwoHasPendingEdit(false);
    setStepTwoError("");
    if (!preserveNotice) setStepTwoNotice("");
    setMatching(null);
    setPreview(null);
    try {
      const matchingResponse = await getSalesImportMatching(activeDraft.id, {
        signal: controller.signal,
      });
      if (controller.signal.aborted || requestId !== stepTwoRequestId.current) return;
      const nextMatching = normalizeSalesImportMatching(matchingResponse);
      if (descriptionOrderRef.current.draftId !== activeDraft.id) {
        descriptionOrderRef.current = {
          draftId: activeDraft.id,
          descriptions: orderSalesImportDescriptions(nextMatching.descriptions)
            .map((description) => description.normalizedDescription),
        };
      }
      setMatching(nextMatching);
      setDraft((current) => current?.id === activeDraft.id
        ? { ...current, version: nextMatching.version || current.version }
        : current);
      const previewResponse = await recalculateSalesImportPreview(
        activeDraft.id,
        nextMatching.version,
        { signal: controller.signal }
      );
      if (controller.signal.aborted || requestId !== stepTwoRequestId.current) return;
      const nextPreview = normalizeSalesImportPreview(previewResponse);
      setPreview(nextPreview);
      setRegularizationPlan((current) => planMatchesPreview(current, nextPreview) ? current : null);
    } catch (requestError) {
      if (controller.signal.aborted || requestId !== stepTwoRequestId.current) return;
      const code = requestError?.data?.code || requestError?.response?.data?.code;
      if (code === "SALES_IMPORT_VERSION_CONFLICT" && retryVersionConflict) {
        setStepTwoLoading(false);
        setStepTwoNotice("El borrador cambió en otra operación. Recargamos los datos; revisá las resoluciones antes de continuar.");
        await loadStepTwo(activeDraft, {
          preserveNotice: true,
          retryVersionConflict: false,
        });
      } else {
        setStepTwoError(getSalesImportErrorMessage(
          requestError,
          "No se pudieron cargar los productos y el Stock."
        ));
      }
    } finally {
      if (requestId === stepTwoRequestId.current) {
        setStepTwoLoading(false);
        stepTwoController.current = null;
      }
    }
  }

  async function resolveDescription(normalizedDescription, resolution) {
    if (busy || !draft?.id || !matching?.version) return;
    const requestId = ++stepTwoRequestId.current;
    stepTwoController.current?.abort();
    const controller = new AbortController();
    stepTwoController.current = controller;
    setStepTwoSaving(true);
    setStepTwoError("");
    setStepTwoNotice("");
    let resolutionSaved = false;
    try {
      const saved = await saveSalesImportResolutions(
        draft.id,
        { expectedVersion: matching.version, resolutions: [
          { normalizedDescription, ...resolution },
        ] },
        { signal: controller.signal }
      );
      if (controller.signal.aborted || requestId !== stepTwoRequestId.current) return;
      const nextMatching = normalizeSalesImportMatching(saved);
      setMatching(nextMatching);
      setPreview(null);
      setRegularizationPlan(null);
      setTreasuryConfig(null);
      setDraft((current) => current?.id === draft.id
        ? { ...current, version: nextMatching.version }
        : current);
      resolutionSaved = true;
      const response = await recalculateSalesImportPreview(
        draft.id,
        nextMatching.version,
        { signal: controller.signal }
      );
      if (controller.signal.aborted || requestId !== stepTwoRequestId.current) return;
      setPreview(normalizeSalesImportPreview(response));
    } catch (requestError) {
      if (controller.signal.aborted || requestId !== stepTwoRequestId.current) return;
      const code = requestError?.data?.code || requestError?.response?.data?.code;
      if (code === "SALES_IMPORT_VERSION_CONFLICT") {
        setStepTwoSaving(false);
        setStepTwoNotice("El borrador cambió en otra operación. Recargamos los datos; revisá las resoluciones antes de continuar.");
        await loadStepTwo(draft, { preserveNotice: true });
      } else if (resolutionSaved) {
        const detail = getSalesImportErrorMessage(
          requestError,
          "Intentá recalcular nuevamente."
        );
        setStepTwoError(
          `La resolución se guardó, pero no se pudo recalcular Productos y Stock. ${detail}`
        );
      } else {
        setStepTwoNotice(getSalesImportErrorMessage(requestError, "No se pudo guardar la resolución."));
      }
    } finally {
      if (requestId === stepTwoRequestId.current) {
        setStepTwoSaving(false);
        stepTwoController.current = null;
      }
    }
  }

  async function prepareRegularizations() {
    if (busy || !draft?.id || !preview?.previewRevision || !preview?.summary.canContinue || !preview.shortages.length) return;
    const requestId = ++stepTwoRequestId.current;
    stepTwoController.current?.abort();
    const controller = new AbortController();
    stepTwoController.current = controller;
    setStepTwoSaving(true);
    setStepTwoNotice("");
    try {
      const response = await confirmSalesImportRegularizations(draft.id, preview.version, { signal: controller.signal });
      if (controller.signal.aborted || requestId !== stepTwoRequestId.current) return;
      const nextPlan = normalizeSalesImportRegularizationPlan(response);
      if (nextPlan.draftId !== preview.draftId || nextPlan.draftVersion !== preview.version || nextPlan.previewRevision !== preview.previewRevision) {
        setStepTwoNotice("El cálculo de Stock cambió. Recargamos los datos; revisá los faltantes antes de confirmar nuevamente.");
        await loadStepTwo(draft, { preserveNotice: true });
        return;
      }
      setRegularizationPlan(nextPlan);
    } catch (requestError) {
      if (controller.signal.aborted || requestId !== stepTwoRequestId.current) return;
      const code = requestError?.data?.code || requestError?.response?.data?.code;
      if (["SALES_IMPORT_VERSION_CONFLICT", "SALES_IMPORT_PREVIEW_STALE", "SALES_IMPORT_PREVIEW_REQUIRED"].includes(code)) {
        setStepTwoSaving(false);
        setStepTwoNotice("El borrador o el cálculo de Stock cambió. Recargamos los datos; revisá los faltantes antes de confirmar nuevamente.");
        await loadStepTwo(draft, { preserveNotice: true });
      } else {
        setStepTwoNotice(getSalesImportErrorMessage(requestError, "No se pudieron preparar las regularizaciones."));
      }
    } finally {
      if (requestId === stepTwoRequestId.current) {
        setStepTwoSaving(false);
        stepTwoController.current = null;
      }
    }
  }

  function goToStepTwo() {
    if (busy || status !== "success" || !draft?.id) return;
    setCurrentStep(2);
    loadStepTwo(draft);
  }

  function goBackToFile() {
    stepTwoRequestId.current += 1;
    stepTwoController.current?.abort();
    stepTwoController.current = null;
    setStepTwoLoading(false);
    setStepTwoHasPendingEdit(false);
    setStepTwoNotice("");
    setCurrentStep(1);
  }

  function goBack() {
    if (currentStep === 4) {
      setCurrentStep(3);
      return;
    }
    if (currentStep === 3) {
      setCurrentStep(2);
      return;
    }
    goBackToFile();
  }

  function goForward() {
    if (currentStep === 1) {
      goToStepTwo();
      return;
    }
    if (currentStep === 2 && !busy && !stepTwoHasPendingEdit && stepTwoReady) setCurrentStep(3);
    if (currentStep === 3 && !busy && treasuryReady) setCurrentStep(4);
    if (currentStep === 4 && !busy && readyToIntegrate) {
      setIntegrationError("");
      setIntegrationOpen(true);
    }
  }

  async function confirmIntegration() {
    if (integrationInFlight.current || !integrationOpen || !readyToIntegrate) return;
    integrationInFlight.current = true;
    setIntegrationLoading(true);
    setIntegrationError("");
    const activeDraft = draft;
    const signature = integrationPreparationSignature(activeDraft, preview, regularizationPlan, treasuryConfig);
    const key = integrationKey.current?.signature === signature
      ? integrationKey.current.requestId
      : integrationRequestId(activeDraft.id);
    integrationKey.current = { signature, requestId: key };
    const controller = new AbortController();
    integrationController.current = controller;
    try {
      const result = await integrateSalesImport(activeDraft.id, { expectedVersion: activeDraft.version, requestId: key }, { signal: controller.signal });
      if (controller.signal.aborted) return;
      navigationAllowedRef.current = true;
      setIntegrationOpen(false);
      navigate(SALES_IMPORTS_PATH, {
        state: {
          ...(returnListState ? { [LIST_STATE_KEY]: returnListState } : {}),
          successMessage: "Las ventas fueron integradas correctamente.",
          ...(Number.isSafeInteger(Number(result?.salesImportId)) && Number(result.salesImportId) > 0
            ? { recentSalesImportId: Number(result.salesImportId) }
            : {}),
        },
      });
    } catch (requestError) {
      if (controller.signal.aborted) return;
      const code = integrationErrorCode(requestError);
      if (["SALES_IMPORT_STOCK_CONFLICT", "SALES_IMPORT_PREVIEW_STALE", "SALES_IMPORT_PREVIEW_REQUIRED"].includes(code)) {
        integrationKey.current = null;
        setIntegrationOpen(false);
        setCurrentStep(2);
        setStepTwoNotice("El Stock o la planificación cambiaron. Revisá los faltantes y prepará nuevamente las regularizaciones.");
        await loadStepTwo(activeDraft, { preserveNotice: true });
      } else if (code === "SALES_IMPORT_TREASURY_CONFLICT") {
        integrationKey.current = null;
        setIntegrationOpen(false);
        setTreasuryConfig(null);
        setCurrentStep(3);
        setStepThreeNotice("La configuración de Tesorería cambió. Revisá el período, la categoría y las cuentas antes de continuar.");
      } else if (["SALES_IMPORT_DUPLICATE_CONFLICT", "SALES_IMPORT_RECEIPT_OVERLAP", "SALES_IMPORT_CONCURRENT_CONFLICT", "SALES_IMPORT_ALREADY_INTEGRATED", "SALES_IMPORT_DRAFT_EXPIRED", "SALES_IMPORT_NOT_DRAFT", "SALES_IMPORT_RECEIPTS_INCOMPLETE"].includes(code)) {
        integrationKey.current = null;
        setIntegrationOpen(false);
        setCurrentStep(1);
        setStatus("error");
        setError(normalizeSalesImportUploadError(requestError));
      } else if (code === "SALES_IMPORT_VERSION_CONFLICT") {
        integrationKey.current = null;
        setIntegrationOpen(false);
        setTreasuryConfig(null);
        setCurrentStep(2);
        setStepTwoNotice("El borrador cambió en otra operación. Revisá Productos y Stock y volvé a confirmar Tesorería.");
        await loadStepTwo(activeDraft, { preserveNotice: true });
      } else if (code === "SALES_IMPORT_REQUEST_ID_REUSED") {
        integrationKey.current = null;
        setIntegrationError("La identificación del intento ya estaba en uso. Volvé a confirmar para generar una nueva solicitud.");
      } else {
        setIntegrationError(getSalesImportErrorMessage(requestError, "No se pudo confirmar la integración. Podés reintentar sin duplicar movimientos."));
      }
    } finally {
      if (integrationController.current === controller) integrationController.current = null;
      integrationInFlight.current = false;
      setIntegrationLoading(false);
    }
  }

  function requestExit() {
    if (busy) return;
    if (!hasActiveDraft) {
      navigationAllowedRef.current = true;
      navigateToList();
      return;
    }
    setOperationError("");
    setDialogMode("exit");
  }

  function closeDialog() {
    if (cleanupLoading) return;
    if (dialogMode === "blocked") navigationBlocker.reset?.();
    setDialogMode(null);
    setOperationError("");
  }

  function completeExit() {
    navigationAllowedRef.current = true;
    setDialogMode(null);
    if (navigationBlocker.state === "blocked") {
      navigationBlocker.proceed?.();
    } else {
      navigateToList();
    }
  }

  function navigateToList() {
    if (returnListState) {
      navigate(SALES_IMPORTS_PATH, {
        state: { [LIST_STATE_KEY]: returnListState },
      });
      return;
    }
    navigate(SALES_IMPORTS_PATH);
  }

  async function confirmDiscard() {
    if (!draft?.id || cleanupLoading) return;
    setOperationError("");
    const deletion = await deleteDraft(draft.id);
    if (!deletion.deleted) return;

    if (dialogMode === "remove") {
      clearDraftState();
      setDialogMode(null);
      return;
    }
    completeExit();
  }

  const leftPanel = (
    <Stack spacing={2}>
      <SalesImportFilePanel
        file={file}
        busy={busy}
        error={dialogMode ? "" : operationError}
        onSelect={handleFile}
        onRemove={() => {
          if (!hasActiveDraft) {
            clearDraftState();
            return;
          }
          setOperationError("");
          setDialogMode("remove");
        }}
      />
      <SalesImportDraftSummary draft={draft} />
    </Stack>
  );

  return (
    <>
      <SalesImportWizardShell
        leftPanel={leftPanel}
        currentStep={currentStep}
        busy={busy}
        backDisabled={currentStep === 1 || (currentStep === 2 && stepTwoHasPendingEdit)}
        continueDisabled={currentStep === 4 ? !readyToIntegrate : (currentStep === 3 ? !treasuryReady : (currentStep === 2 ? !stepTwoReady || stepTwoHasPendingEdit : status !== "success" || !draft?.id))}
        primaryLabel={integrationLoading ? "Integrando ventas…" : status === "uploading" ? "Verificando archivo…" : currentStep === 4 ? "Integrar" : "Continuar"}
        onExit={requestExit}
        onCancel={requestExit}
        onBack={goBack}
        onContinue={goForward}
      >
        {currentStep === 1 ? (
          <SalesImportStepOne status={status} draft={draft} error={error} />
        ) : currentStep === 2 ? (
          <SalesImportStepTwo
            matching={matching}
            preview={preview}
            plan={regularizationPlan}
            descriptionOrder={descriptionOrderRef.current.descriptions}
            rememberedResolutions={rememberedStockResolutions.current}
            loading={stepTwoLoading}
            saving={stepTwoSaving}
            error={stepTwoError}
            onRetry={() => loadStepTwo(draft)}
            notice={stepTwoNotice}
            options={productOptions}
            optionsLoading={productOptionsLoading}
            optionsError={productOptionsError}
            productSearch={productSearch}
            onRetryOptions={() => setProductOptionsRetry((value) => value + 1)}
            onSearch={setProductSearch}
            onResolve={resolveDescription}
            onPrepare={prepareRegularizations}
            onPendingEditChange={setStepTwoHasPendingEdit}
          />
        ) : currentStep === 3 ? (
          <SalesImportStepThree draft={draft} config={treasuryConfig} form={treasuryForm} notice={stepThreeNotice} onFormChange={setTreasuryForm} onConfigured={(value) => { setTreasuryConfig(value); if (value?.valid && value?.persisted) setStepThreeNotice(""); }} onSavingChange={setStepThreeSaving} />
        ) : <SalesImportStepFour draft={draft} preview={preview} treasury={treasuryConfig} readyToIntegrate={readyToIntegrate} />}
      </SalesImportWizardShell>

      <SalesImportDiscardDialog
        open={Boolean(dialogMode)}
        mode={dialogMode === "remove" ? "remove" : "exit"}
        loading={cleanupLoading}
        error={operationError}
        onClose={closeDialog}
        onConfirm={confirmDiscard}
        onForceExit={completeExit}
      />
      <SalesImportIntegrationDialog
        open={integrationOpen}
        draft={draft}
        regularizations={preview?.shortages?.length || 0}
        movements={treasuryConfig?.allocations?.length || 0}
        loading={integrationLoading}
        error={integrationError}
        onClose={() => { if (!integrationLoading) setIntegrationOpen(false); }}
        onConfirm={confirmIntegration}
      />
    </>
  );
}
